// ============================================
// COTIZACIONESPARECIDAS.JS — "esto ya lo cotizamos una vez"
// ============================================
// Hay 148 cotizaciones hechas. La mayoria del trabajo de Beep se repite: otra recompra de
// CCTV, otra cerca electrica, otro rack. Volver a armarla renglon por renglon es tirar ese
// trabajo a la basura.
//
// EL CRITERIO DE PARECIDO vive en el servidor (services/cotizarRapido.js) y NO es el que
// parecia obvio. Se midio antes de construirlo: **Bodegas Alianza tiene 75 de las 148
// cotizaciones (51%)**, asi que "mismo cliente" empata con media base y no ordena nada.
// Manda el TIPO DE TRABAJO; el cliente suma pero no decide. Cada tarjeta dice POR QUE
// salio, para que nadie tenga que fiarse a ciegas.
//
// Si no hay ninguna parecida, este componente devuelve null: nada de cajas vacias.
//
// COTIZACIONES-PARECIDAS (2026-10-05, Ricardo): va DEBAJO de las cotizaciones del propio
// expediente y arranca PLEGADO en una sola linea — es una sugerencia, no lo primero que hay
// que ver. Solo viene abierto si el expediente aun no tiene ninguna cotizacion propia,
// que es justo cuando sirve. Las gemelas (mismo total, mismos renglones) las junta el
// servidor y aqui se dice "y N mas igual".

import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../../config/api';
import { authHeaders } from '../../utils/storage';
import useResponsive from '../../hooks/useResponsive';
import { fmtDinero } from '../../utils/dinero';

const PP = "'Poppins', sans-serif";
const AZUL = '#265F97';
const GRIS = '#5e666e';
const GRIS_CLARO = '#9aa3ab';
const BORDE = '#e6e8ea';

const pesos = (n) => fmtDinero(n);
const fecha = (v) => {
  const d = new Date(v);
  return isNaN(d) ? '' : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: '2-digit' });
};
const COLOR_ESTADO = {
  autorizada: '#1b9e6d', enviada: '#265F97', rechazada: '#c0392b', borrador: '#9aa3ab',
};

export default function CotizacionesParecidas({ expedienteId, onCreada }) {
  const [parecidas, setParecidas] = useState(null);
  const [duplicando, setDuplicando] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  // null = aun no se decidio; se decide UNA vez con la primera carga y luego manda el usuario
  const [abierto, setAbierto] = useState(null);
  const { isMobile } = useResponsive();

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/cotizaciones/rapida/${expedienteId}/opciones`, { headers: authHeaders() })
        .then(x => (x.ok ? x.json() : null));
      // Sin permiso de finanzas el servidor responde 403: la seccion simplemente no sale.
      if (r && r.ok) {
        setParecidas(r.parecidas || []);
        setAbierto(prev => (prev == null ? !(Number(r.propias) > 0) : prev));
      } else setParecidas([]);
    } catch { setParecidas([]); }
  }, [expedienteId]);

  useEffect(() => { cargar(); }, [cargar]);

  const duplicar = async (cot) => {
    setDuplicando(cot.id); setError(null); setAviso(null);
    try {
      const r = await fetch(`${API_BASE}/api/cotizaciones/rapida/duplicar`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cotizacion_id: cot.id, oportunidad_id: expedienteId }),
      }).then(x => x.json());
      if (!r.ok) { setError(r.error || 'No se pudo duplicar.'); return; }
      if (r.descartadas && r.descartadas.length) {
        setAviso(`${r.folio} creada, pero ${r.descartadas.length} renglon(es) no se pudieron traer porque ya no estan en el catalogo.`);
      }
      onCreada && onCreada(r);
      // Se abre el editor grande: "Duplicar y AJUSTAR" — la gracia es afinarla.
      window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: r.cotizacion_id } }));
    } catch { setError('No se pudo conectar.'); }
    finally { setDuplicando(null); }
  };

  if (!parecidas || parecidas.length === 0) return null;   // punto 13: nada de cajas vacias

  return (
    <div style={{
      background: '#fff', border: `1px solid ${BORDE}`, borderRadius: 12,
      padding: isMobile ? '4px 14px' : '4px 16px', fontFamily: PP, marginTop: 14,
    }}>
      {/* Cabecera = boton de plegar. Una sola linea tambien a 360 px: el texto se corta
          con puntos suspensivos antes que partirse en dos. */}
      <button
        type="button"
        onClick={() => setAbierto(a => !a)}
        aria-expanded={!!abierto}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
          padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: PP, textAlign: 'left', minWidth: 0,
        }}
      >
        <span style={{
          flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: '#2e3338',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          Cotizaciones parecidas ({parecidas.length})
        </span>
        <span aria-hidden="true" style={{
          fontSize: 13, color: GRIS, flexShrink: 0,
          transform: abierto ? 'rotate(90deg)' : 'none', transition: 'transform .15s',
        }}>▸</span>
      </button>

      {abierto && (<div style={{ paddingBottom: isMobile ? 10 : 12 }}>
      <div style={{ fontSize: 12, color: GRIS_CLARO, marginBottom: 12, lineHeight: 1.4 }}>
        Trabajos parecidos que ya cotizamos antes, sugeridos por el sistema. Al duplicar se recalcula con los precios de hoy.
      </div>

      {aviso && (
        <div style={{ background: '#fff8ed', border: '1px solid #fce5b8', borderRadius: 8,
                      padding: '8px 10px', fontSize: 12.5, color: '#8a5a1b', marginBottom: 10 }}>
          {aviso}
        </div>
      )}
      {error && (
        <div style={{ background: '#fdecec', border: '1px solid #f3c9c9', borderRadius: 8,
                      padding: '8px 10px', fontSize: 12.5, color: '#8a2b2b', marginBottom: 10 }}>
          {error}
        </div>
      )}

      {parecidas.map(c => (
        <div key={c.id} style={{
          border: `1px solid ${BORDE}`, borderRadius: 10, padding: 12, marginBottom: 10,
        }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <strong style={{ fontSize: 13.5, color: '#2e3338' }}>{c.folio}</strong>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
              background: '#f4f5f6', color: COLOR_ESTADO[c.estado] || GRIS,
            }}>{c.estado}</span>
            <span style={{ fontSize: 12, color: GRIS_CLARO }}>{fecha(c.creado_at)}</span>
            <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 800, color: '#2e3338' }}>
              {pesos(c.total)}
            </span>
          </div>

          {/* El cliente es el del EXPEDIENTE de esa cotizacion (la cotizacion no guarda
              cliente propio). Si no lo hay, no se pinta nada: "Sin cliente" confundia. */}
          {(c.cliente || c.asunto) && (
            <div style={{ fontSize: 12.5, color: GRIS, marginTop: 2, wordBreak: 'break-word' }}>
              {[c.cliente, c.asunto].filter(Boolean).join(' · ')}
            </div>
          )}

          {/* Los 3 primeros renglones: es lo que deja juzgar si sirve sin abrirla */}
          {Array.isArray(c.primeras_lineas) && c.primeras_lineas.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: GRIS }}>
              {c.primeras_lineas.map((l, i) => (
                <li key={i} style={{ marginBottom: 2, wordBreak: 'break-word' }}>
                  {Number(l.cantidad) !== 1 && <strong>{Number(l.cantidad)} × </strong>}
                  {l.nombre}
                </li>
              ))}
            </ul>
          )}

          {/* Por que salio esta: sin esto, una sugerencia es magia y nadie se fia */}
          {c.porque && c.porque.length > 0 && (
            <div style={{ fontSize: 11.5, color: GRIS_CLARO, marginTop: 8 }}>
              Se parece por: {c.porque.join(' · ')}
            </div>
          )}

          {c.iguales > 0 && (
            <div style={{ fontSize: 11.5, color: GRIS_CLARO, marginTop: 4 }}>
              y {c.iguales} más igual{c.iguales > 1 ? 'es' : ''}
            </div>
          )}

          <button
            onClick={() => duplicar(c)}
            disabled={duplicando != null}
            style={{
              marginTop: 10, width: isMobile ? '100%' : 'auto',
              padding: '10px 16px', minHeight: 44, borderRadius: 10,
              border: `1px solid ${AZUL}`, background: duplicando === c.id ? '#cfd6dd' : '#fff',
              color: duplicando === c.id ? '#fff' : AZUL,
              fontFamily: PP, fontSize: 13.5, fontWeight: 700,
              cursor: duplicando != null ? 'default' : 'pointer',
            }}
          >
            {duplicando === c.id ? 'Duplicando…' : 'Duplicar y ajustar'}
          </button>
        </div>
      ))}
      </div>)}
    </div>
  );
}
